// components/profile/SSNVerificationModal.tsx
import React, { useState } from "react";
import {
  X,
  Shield,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Clock,
} from "lucide-react";
import { SSNVerificationPayload } from "../../lib/types/authOnboarding";

interface SSNVerificationModalProps {
  onClose: () => void;
  onComplete: (data: SSNVerificationPayload) => Promise<boolean>;
  isPending: boolean;
}

const SSNVerificationModal: React.FC<SSNVerificationModalProps> = ({
  onClose,
  onComplete,
  isPending,
}) => {
  const [step, setStep] = useState(1);
  const [ssn, setSSN] = useState(["", "", ""]);
  const [showSSN, setShowSSN] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  //   const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const totalSteps = 3;

  const handleSSNChange = (index: number, value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/\D/g, "");
    const maxLength = index === 0 ? 3 : index === 1 ? 2 : 4;
    const truncatedValue = numericValue.slice(0, maxLength);

    const newSSN = [...ssn];
    newSSN[index] = truncatedValue;
    setSSN(newSSN);
    setError("");

    // Auto-focus next input
    if (truncatedValue.length === maxLength && index < 2) {
      const nextInput = document.getElementById(`ssn-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && ssn[index] === "" && index > 0) {
      const prevInput = document.getElementById(`ssn-${index - 1}`);
      prevInput?.focus();
    }
  };

  const isSSNComplete =
    ssn[0].length === 3 && ssn[1].length === 2 && ssn[2].length === 4;

  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (!isSSNComplete) {
        setError("Please enter a complete SSN");
        return;
      }
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!confirmChecked) {
      setError("Please confirm that the information is accurate");
      return;
    }

    setError("");
    const mergedSSN = `${ssn[0] + ssn[1] + ssn[2]}`;
    await onComplete({ ssn: mergedSSN });
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
              s < step
                ? "bg-emerald-500 text-white"
                : s === step
                  ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white"
                  : "bg-gray-100 text-gray-400"
            }`}
          >
            {s < step ? <CheckCircle className="w-4 h-4" /> : s}
          </div>
          {s < totalSteps && (
            <div
              className={`w-12 h-1 rounded-full transition-all ${
                s < step ? "bg-emerald-500" : "bg-gray-100"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="text-center">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center mb-6">
        <Shield className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">SSN Verification</h3>
      <p className="text-gray-500 mb-6">
        To comply with federal regulations and enable payments, we need to
        verify your Social Security Number.
      </p>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-left mb-6">
        <h4 className="font-medium text-blue-800 mb-2">Why we need this:</h4>
        <ul className="text-sm text-blue-700 space-y-2">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Required for tax reporting (1099-NEC)</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Enables direct deposit payments</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Verifies your identity for security</span>
          </li>
        </ul>
      </div>

      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl text-left">
        <Lock className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500">
          Your SSN is encrypted using AES-256 and stored securely. We never
          share your SSN with third parties except as required by law.
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
        Enter Your SSN
      </h3>
      <p className="text-gray-500 mb-6 text-center">
        Please enter your 9-digit Social Security Number
      </p>

      {/* SSN Input */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <div className="relative">
          <input
            id="ssn-0"
            type={showSSN ? "text" : "password"}
            value={ssn[0]}
            onChange={(e) => handleSSNChange(0, e.target.value)}
            onKeyDown={(e) => handleKeyDown(0, e)}
            placeholder="•••"
            maxLength={3}
            className="w-20 h-14 text-center text-xl font-mono border-2 border-gray-200 rounded-xl focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all"
          />
        </div>
        <span className="text-2xl text-gray-300">-</span>
        <div className="relative">
          <input
            id="ssn-1"
            type={showSSN ? "text" : "password"}
            value={ssn[1]}
            onChange={(e) => handleSSNChange(1, e.target.value)}
            onKeyDown={(e) => handleKeyDown(1, e)}
            placeholder="••"
            maxLength={2}
            className="w-16 h-14 text-center text-xl font-mono border-2 border-gray-200 rounded-xl focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all"
          />
        </div>
        <span className="text-2xl text-gray-300">-</span>
        <div className="relative">
          <input
            id="ssn-2"
            type={showSSN ? "text" : "password"}
            value={ssn[2]}
            onChange={(e) => handleSSNChange(2, e.target.value)}
            onKeyDown={(e) => handleKeyDown(2, e)}
            placeholder="••••"
            maxLength={4}
            className="w-24 h-14 text-center text-xl font-mono border-2 border-gray-200 rounded-xl focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all"
          />
        </div>
      </div>

      {/* Show/Hide Toggle */}
      <button
        onClick={() => setShowSSN(!showSSN)}
        className="flex items-center gap-2 mx-auto text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6"
      >
        {showSSN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        {showSSN ? "Hide SSN" : "Show SSN"}
      </button>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Security Note */}
      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
        <Shield className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500">
          This connection is encrypted and secure. Your SSN will be verified and
          securely stored.
        </p>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
        Confirm & Submit
      </h3>
      <p className="text-gray-500 mb-6 text-center">
        Please review and confirm your information
      </p>

      {/* Summary Card */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-500">Social Security Number</span>
          <span className="font-mono text-gray-900">•••-••-{ssn[2]}</span>
        </div>
        <div className="h-px bg-gray-200 my-3" />
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Verification Status</span>
          <span className="text-sm font-medium text-amber-600 flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Will be pending
          </span>
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <label className="flex items-start gap-3 p-4 bg-white border-2 border-gray-200 rounded-xl cursor-pointer hover:border-[#8B52FF]/50 transition-colors mb-4">
        <input
          type="checkbox"
          checked={confirmChecked}
          onChange={(e) => {
            setConfirmChecked(e.target.checked);
            setError("");
          }}
          className="w-5 h-5 rounded border-gray-300 text-[#8B52FF] focus:ring-[#8B52FF] mt-0.5"
        />
        <span className="text-sm text-gray-600">
          I confirm that the SSN provided is accurate and belongs to me. I
          understand that providing false information may result in account
          termination.
        </span>
      </label>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Processing Time Note */}
      <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
        <Clock className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          Verification typically takes 1-2 business days. You'll receive an
          email once the verification is complete.
        </p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">SSN Verification</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {renderStepIndicator()}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
          {step > 1 ? (
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={step === 2 && !isSSNComplete}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isPending || !confirmChecked}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Verification
                  <CheckCircle className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SSNVerificationModal;
